import React, { Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationCircleIcon } from '@heroicons/react/outline'
import * as Yup from 'yup'
import { Field, Formik, FormikProps, Form } from 'formik'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-hot-toast'

import { Customer } from '../../types/customer'
import { graphQLClient } from '../../services/authService'
import customerQueries from '../../services/graphql/customers/queries'
import placeService from '../../services/placeService'

import FormikAsyncSelect from '../FormikAsyncSelect'
import Spinner from '../Spinner'

type ClaimModalProps = {
  placeId: string
  open: boolean
  onClose: () => void
}

type ClaimForm = {
  customer: Customer | undefined
}

const schema = Yup.object().shape({
  customer: Yup.object(),
})

const initialValues: ClaimForm = {
  customer: undefined,
}

const ClaimModal: React.FC<ClaimModalProps> = (props) => {
  const cancelButtonRef = useRef(null)
  const queryClient = useQueryClient()
  const claim = useMutation(placeService.claimPlaceById, {
    onSuccess: async (data) => {
      await queryClient.refetchQueries(['location', data.claimPlace.id])
      props.onClose()
    },
  })

  const handleOnSubmit = async (values: ClaimForm) => {
    if (!values.customer?.id) {
      return
    }

    try {
      await claim.mutateAsync({
        customerId: values.customer.id,
        placeId: props.placeId,
      })
    } catch (e) {
      const convertedError = e as { response: { errors: Array<{ message: string }> } }
      convertedError.response.errors.forEach((error) => {
        toast.error(error.message)
      })
    }
  }

  const customerOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    const response = await graphQLClient.request(customerQueries.getAll, {
      where: { name_contains: inputValue },
    })
    return response.customers
  }

  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={props.onClose}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleOnSubmit}
        >
          {({ isSubmitting, dirty }: FormikProps<ClaimForm>) => (
            <Form className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              </Transition.Child>

              {/* This element is to trick the browser into centering the modal contents. */}
              <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
                &#8203;
              </span>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                        <ExclamationCircleIcon className="h-6 w-6 text-yellow-800" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                          Assign to Customer?
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to assign to this customer? It will allow them to manage this
                            location.
                          </p>
                        </div>
                      </div>
                    </div>
                    <Field
                      name="customer"
                      component={FormikAsyncSelect}
                      label="Customer"
                      loadOptions={customerOptions}
                      getOptionLabel={(item: Customer) => item.name}
                      getOptionValue={(item: Customer) => item.id}
                      placeholder="Select Customer"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      disabled={isSubmitting || !dirty}
                      type="submit"
                      className="btn-primary inline-flex w-full justify-center rounded-md border border-transparent bg-brand-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {claim.isLoading ? (
                        <span className="flex">
                          <Spinner className="h-5 w-5 text-gray-700" aria-hidden="true" /> Loading
                        </span>
                      ) : (
                        'Assign'
                      )}
                    </button>
                    <button
                      disabled={isSubmitting}
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={props.onClose}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Transition.Child>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Transition.Root>
  )
}

export default ClaimModal
